import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@fontsource-variable/instrument-sans";
import "@fontsource-variable/jetbrains-mono";
import "@app/design.css";
import { getUserMeQueryOptions } from "@api/generated/user/user";
import { getWorkspaceSettingsQueryOptions } from "@api/generated/workspace/workspace";
import { queryClient } from "@app/queryClient";
import { buildTheme } from "@app/theme";
import { currentLanguage, i18nReady } from "@i18n";
import { MantineProvider } from "@mantine/core";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { routeTree } from "./routeTree.gen";

const router = createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/** The UI in the current language: a language switch rebuilds the theme (input hints, decimal separator) and remounts
 *  the screens, so texts computed once per mount follow too; the query cache stays. */
function Root() {
  useTranslation(); // re-render on a language switch
  const lang = currentLanguage();
  // biome-ignore lint/correctness/useExhaustiveDependencies: the theme's texts depend on the language
  const theme = useMemo(() => buildTheme(), [lang]);
  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <QueryClientProvider client={queryClient}>
        <RouterProvider key={lang} router={router} />
      </QueryClientProvider>
    </MantineProvider>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");
// the session and the settings load while the first paint waits for the texts of the language this browser last used
void queryClient.prefetchQuery(getUserMeQueryOptions());
void queryClient.prefetchQuery(getWorkspaceSettingsQueryOptions());
void i18nReady.then(() =>
  createRoot(root).render(
    <StrictMode>
      <Root />
    </StrictMode>,
  ),
);
